import _ from 'underscore';
import { partialConversionPairs } from '@bingads-webui-campaign/column-conversions';
import { Defaults } from './constants';
import { ChartGranularityAdapter } from './chart-granularity-adapter';
import { ChartMetricsAdapter } from './chart-metrics-adapter';

const patchPreference = (metricsMap, permissions = {}) => {
  const {
    ColumnConversion: IsColumnConversionEnabled,
    PartialConversions: IsPartialConversionsEnabled,
  } = permissions.dynamic || {};

  const metricsMapIdIndex = _.indexBy(metricsMap, 'id');
  const metricsMapNameIndex = _.indexBy(metricsMap, 'name');
  const conversionPropertyMap = IsPartialConversionsEnabled ?
    partialConversionPairs :
    _.invert(partialConversionPairs);

  return IsColumnConversionEnabled ?
    (srcMetricId) => {
      if (_.has(metricsMapIdIndex, srcMetricId)) {
        const srcMetricName = metricsMapIdIndex[srcMetricId].name;
        if (_.has(conversionPropertyMap, srcMetricName)) {
          const targetMetricName = conversionPropertyMap[srcMetricName];
          const targetMetricId = metricsMapNameIndex[targetMetricName].id;
          return targetMetricId;
        }
      }
      return srcMetricId;
    } :
    _.identity;
};

export class ChartDataAdapter {
  constructor({
    i18n, permissions = {}, gridName, lowestSupportedGranularity, campaignType,
  } = {}) {
    this.i18n = i18n;
    this.campaignType = campaignType;
    this.granularityAdapter = new ChartGranularityAdapter({ i18n, gridName, lowestSupportedGranularity });
    this.metricsAdapter = new ChartMetricsAdapter(i18n, campaignType);
    ({
      ColumnConversion: this.IsColumnConversionEnabled,
      PartialConversions: this.IsPartialConversionsEnabled,
      InlineChartColumnMerticsEnabled: this.inlineChartColumnMerticsEnabled,
    } = permissions.dynamic || {});
    this.patchPreference = patchPreference(this.metricsAdapter.metricsMap, permissions);
  }

  /**
   * Gets chart title.
   * @param {object} state - sate instance to get the current level.
   * @returns {string} localized chart title.
   */
  getChartTitle = (state) => {
    let currentLevel = 'BI_Chart_AccountLevel';
    if (state.get('adgroup_id')) {
      currentLevel = 'BI_Chart_AdGroupLevel';
    } else if (state.get('campaign_id')) {
      currentLevel = 'BI_Chart_CampaignLevel';
    }
    return this.i18n.getString(currentLevel);
  };

  /**
   * Gets localized text for no data text.
   * @param {boolean} isError - flag to return error string
   * @returns {string} localized no data text
   */
  getNoDataText = (isError = false) => (isError ?
    this.i18n.getString('BI_Chart_Error') :
    this.i18n.getString('BI_Chart_NoData'));

  /**
   * @param {object} data - series data from which start and end data
   * @returns {object} first data item
   */
  getFirstDataItem = data => _.values(data)[0];

  /**
   * @param {object} dataItem data item
   * @returns {boolean} is dataItem.PrimaryData available
   */
  isPrimaryDataAvailable = dataItem =>
    _.isObject(dataItem)
    && _.isArray(dataItem.PrimaryData)
    && !_.isEmpty(dataItem.PrimaryData)
    && _.every(dataItem.PrimaryData, primaryDataItem => _.isArray(primaryDataItem));

  /**
   * @param {object} dataItem data item
   * @returns {boolean} is dataItem.ComparisonData available
   */
  isComparisonDataAvailable = dataItem =>
    _.isObject(dataItem)
    && _.isArray(dataItem.ComparisonData)
    && !_.isEmpty(dataItem.ComparisonData)
    && _.every(dataItem.ComparisonData, comparisonDataItem => _.isArray(comparisonDataItem));

  /**
   * Gets localized chart start and end date labels. By default the
   * show lables will be true and further after aligning fluctuation, if
   * fluctuation data overlaps with start/end date, date will be hidden.
   * @param {object} data - series data from which start and end data
   *  to be shown in label is extracted.
   * @param {number} granularityId - Id of the current grain selection.
   * @returns {object} localized chart label
   */
  getChartLabel = (data, granularityId) => {
    const firstDataItem = this.getFirstDataItem(data);
    const isComparisonDataAvailable = this.isComparisonDataAvailable(firstDataItem);
    const xAxisStartDate = firstDataItem.PrimaryData[0][2];
    const xAxisEndDate = this.getLastAvailableDate(firstDataItem.PrimaryData);
    const xAxisCompareStartDate = isComparisonDataAvailable ?
      firstDataItem.ComparisonData[0][2] :
      '';
    const xAxisCompareEndDate = isComparisonDataAvailable ?
      this.getLastAvailableDate(firstDataItem.ComparisonData) :
      '';

    return {
      primaryStartDate: this.formatDate(xAxisStartDate, granularityId),
      primaryEndDate: this.formatDate(xAxisEndDate, granularityId),
      comparisonStartDate: this.formatDate(xAxisCompareStartDate, granularityId),
      comparisonEndDate: this.formatDate(xAxisCompareEndDate, granularityId),
    };
  };

  /**
   * Gets last available date.
   * @param {object} data - series data from which start and end data
   *  to be shown in label is extracted.
   * @returns {string} - latest available date within given data
   */
  getLastAvailableDate(data) {
    if (!data[data.length - 1][2]) {
      for (let i = data.length - 1; i > -1; i -= 1) {
        if (data[i][2]) {
          return data[i][2];
        }
      }
    }
    return data[data.length - 1][2];
  }

  /**
   * Gets localized list of metrics.
   * @param {object} permission the Persission obj
   * @param {function} metricsFilter the filter function of metricsList
   * @param {object} customMetricNameList the custom metrics list
   * @param {string} currentEntity the current entity of the page
   * @returns {object} localized list of metric items as {id, value}
   */
  getPrimaryMetricsList = (permission = {}, metricsFilter = null, customMetricNameList = [], currentEntity) =>
    this.metricsAdapter.getMetricsList(_.extend(permission, {
      hasNone: false,
      metricsFilter,
      customMetricNameList,
      currentEntity,
    }));

  /**
   * Gets localized list of metrics with none.
   * @param {object} permission the Persission obj
   * @param {function} metricsFilter the filter function of metricsList
   * @param {object} customMetricNameList the custom metrics list
   * @param {string} currentEntity the current entity of the page
   * @returns {object} localized list of metric items as {id, value}
   */
  getSecondaryMetricsList = (permission = {}, metricsFilter = null, customMetricNameList = [], currentEntity) =>
    this.metricsAdapter.getMetricsList(_.extend(permission, {
      hasNone: true,
      metricsFilter,
      customMetricNameList,
      currentEntity,
    }));

  /**
   * Gets localized metric item corresponding to the id.
   * @param {number} id - Id corresponding to the metric item
   * @returns {object} localized list of metric items as {id, value}
   */
  getMetricItem = id => this.metricsAdapter.getMetricItem(id);

  /**
   * Gets localized metric name corresponding to the id.
   * @param {number} id - Id corresponding to the metric item
   * @returns {object} localized list of metric items as {id, value}
   */
  getMetricName = id => this.metricsAdapter.getMetricNameFromId(id);

  /**
   * Gets localized metric value formatted using i18n based formatters.
   * @param {number} id - Id corresponding to the metric item
   * @param {number} value - value of the metric
   * @param {object} options - i18n formatting options
   * @returns {object} localized list of metric items as {id, value}
   */
  formatMetric = (id, value, options = {}) => {
    const formatter = this.metricsAdapter.getMetricFormatter(id);
    return formatter(value, options);
  };

  /**
   * Gets localized granularity item corresponding to the id.
   * @param {number} id - Id corresponding to the granularity item
   * @returns {object} localized list of granularity items as {id, value}
   */
  getGranularitySelection = id => this.granularityAdapter.getGranularityItem(id);

  /**
  * Gets localized list of granularities for the current daterange.
  * @returns {object} localized list of granularity items as {id, value}
  */
  getGranularitiesList = () => this.granularityAdapter.getGranularitiesList();

  /**
   * Gets selected granularity item enum
   * @param {number} id - Id corresponding to the granularity item
   * @returns {object} localized list of granularity items as {id, value}
   */
  getGranularityEnum = id => this.granularityAdapter.getGranularityEnumFromId(id);

  /**
   * Gets UI preferences like selected metrics, show chart etc. from preference store.
   * If not found in the preference store, a default value will be returned.
   * @param {object} preference - preference store to be used to fetch data
   * @param {object} pageContext - pagecontext to be used to get the current tab name
   * @returns {object} object containing preferences and defaults
   */
  getPreferencesAndDefaults = (preference, pageContext) =>
    preference.getInlineChartInfo().then((preferenceObject) => {
      const showChartPref = _.result(
        preferenceObject,
        `${pageContext.CurrentTabId}-showChart`,
        Defaults.showChart
      );

      const showMovingAverageInPref = _.result(
        preferenceObject,
        `${pageContext.CurrentTabId}-showMovingAverage`,
        Defaults.showMovingAverage
      );

      const granularityEnumInPref = _.result(
        preferenceObject,
        `${pageContext.CurrentTabId}-granularity`,
        Defaults.granularity
      );
      const granularityIdInPref =
        this.granularityAdapter.getGranularityIdFromEnum(granularityEnumInPref);

      let primaryIdInPref = Defaults.primaryMetricId;
      if (this.IsColumnConversionEnabled) {
        const primaryMetricPrefKey = `${pageContext.CurrentTabId}-primaryMetric`;
        if (_.has(preferenceObject, primaryMetricPrefKey)) {
          primaryIdInPref =
            this.patchPreference(preferenceObject[primaryMetricPrefKey]);
        }
      } else {
        primaryIdInPref = _.result(
          preferenceObject,
          `${pageContext.CurrentTabId}-primaryMetric`,
          Defaults.primaryMetricId
        );
      }

      let secondaryIdInPref = Defaults.secondaryMetricId;
      if (this.IsColumnConversionEnabled) {
        const secondaryMetricPrefKey = `${pageContext.CurrentTabId}-secondaryMetric`;
        if (_.has(preferenceObject, secondaryMetricPrefKey)) {
          secondaryIdInPref =
            this.patchPreference(preferenceObject[secondaryMetricPrefKey]);
        }
      } else {
        secondaryIdInPref = _.result(
          preferenceObject,
          `${pageContext.CurrentTabId}-secondaryMetric`,
          Defaults.secondaryMetricId
        );
      }

      return {
        showChart: showChartPref,
        showMovingAverage: showMovingAverageInPref,
        granularityId: granularityIdInPref,
        primaryMetricId: primaryIdInPref,
        secondaryMetricId: secondaryIdInPref,
      };
    });

  /**
   * Re-evaluate granularity list and selection based on preference and current daterange.
   * @param {object} dateRange - daterange provided by date picker
   * @param {number} preferredGranularityId - Granularity Id from preference store
   * @returns {number} granularity Id to be used in current selection
  */
  reevaluateGranularity = (dateRange, preferredGranularityId) => {
    this.granularityAdapter.updateAvailableGranularities(dateRange);
    return this.granularityAdapter.reconcileSelectedGranularity(preferredGranularityId);
  };

  /**
   * Extracts and normalizes chart data based on individual metric information.
   * @param {object} rawData flag to return error string
   * @returns {string} localized no data text
  */
  prepareData = (rawData) => {
    const transformChartPoints = (series) => {
      const ret = {};

      const convertData = (point, isPrimaryData) => {
        if (isPrimaryData) {
          ret.PrimaryData = ret.PrimaryData || [];
        } else {
          ret.ComparisonData = ret.ComparisonData || [];
        }

        const utcPoint = point;
        // Because the data-time is based on campaign time zone by default,
        // ideally, the format of data-time string in OData response should not
        // contain time zone part. In case it does, we should remove it.
        // e.g. "2016-11-11T00:00:00-08:00" -> "2016-11-11T00:00:00"
        utcPoint.DateTime = point.DateTime.substring(0, 19);

        const dateTime = new Date(utcPoint.DateTime);

        if (isPrimaryData) {
          ret.PrimaryData.push([dateTime.getTime(), utcPoint.Value, point.DateTime]);
        } else {
          ret.ComparisonData.push([dateTime.getTime(), utcPoint.Value, utcPoint.DateTime]);
        }
      };

      _.each(series.PrimaryDataPoints, (point) => {
        convertData(point, true);
      });

      _.each(series.ComparisonDataPoints, (point) => {
        convertData(point, false);
      });

      return ret;
    };

    const ret = {};

    _.each(rawData, (series) => {
      if (!series) {
        return;
      }
      const metricId = this.metricsAdapter.getMetricIdFromName(series.MetricName);

      ret[metricId] = {};
      ret[metricId] = transformChartPoints(series);
    });

    return ret;
  };

  /**
   * Compares two series and fills all the smaller series with null.
   * This is used mostly in POP scenarios with custom date range where the data
   * points are either fewer or more than the primary series.
   * Comparison start date is aligned to primary series start date.
   * @param {object} dataSet - inline chart data
   * @param {string} granularity - current granularity selection
   * @returns {object} aligned data
  */
  alignDataForComparison = (dataSet, granularity) => {
    const keys = Object.keys(dataSet);

    _.each(keys, (key) => {
      const metricData = dataSet[key];

      if (metricData.PrimaryData &&
        metricData.ComparisonData &&
        metricData.PrimaryData.length !== metricData.ComparisonData.length) {
        const shortermetricData =
          metricData.PrimaryData.length > metricData.ComparisonData.length ?
            metricData.ComparisonData :
            metricData.PrimaryData;

        const longermetricData =
          metricData.PrimaryData.length < metricData.ComparisonData.length ?
            metricData.ComparisonData :
            metricData.PrimaryData;

        const diff = Math.abs(metricData.PrimaryData.length - metricData.ComparisonData.length);

        for (let i = 0; i < diff; i++) { // eslint-disable-line no-plusplus
          const diff1 = longermetricData[shortermetricData.length][0]
            - longermetricData[shortermetricData.length - 1][0];
          const dayAfter = shortermetricData[shortermetricData.length - 1][0] + diff1;

          shortermetricData.push([dayAfter, null]);
        }
      }

      // Current Mt returns last day of month, instead of first day of month.
      // Because last day of month
      if (granularity === 'Month' || granularity === 'Quarter') {
        const dayInMillisec = 60 * 60 * 24 * 1000;

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < metricData.PrimaryData.length - 1; i++) {
          const primaryDataDateDiff =
            (metricData.PrimaryData[i + 1][0] - metricData.PrimaryData[i][0]) / dayInMillisec;
          const comparisonDataDateDiff =
            (metricData.ComparisonData[i + 1][0] - metricData.ComparisonData[i][0]) / dayInMillisec;

          if (comparisonDataDateDiff > primaryDataDateDiff) {
            metricData.PrimaryData[i + 1][0] += dayInMillisec;
          } else if (comparisonDataDateDiff < primaryDataDateDiff) {
            metricData.ComparisonData[i + 1][0] += dayInMillisec;
          }
        }
      }
    });
  };

  formatDate = (timestamp, granularityId) => {
    // fix prod err: granularityId can be undefined due to Merged PR 789653
    if (_.isUndefined(granularityId)) {
      return '';
    }

    const options = this.granularityAdapter.getGranularityDateFormat(granularityId);

    if (timestamp && options) {
      if (_.isUndefined(this.dateParser)) {
        this.dateParser = this.i18n.dateParser({ raw: 'yyyy-MM-ddTHH:mm:ss' });
      }

      if (_.isUndefined(this.dateFormatters)) {
        this.dateFormatters = {};
      }

      let dateFormatter = this.dateFormatters[granularityId];
      if (_.isUndefined(dateFormatter)) {
        const formatter = this.i18n.dateFormatter(options);
        dateFormatter = _.compose(formatter, this.dateParser);
        this.dateFormatters[granularityId] = dateFormatter;
      }

      return dateFormatter(timestamp);
    }

    return '';
  };

  /**
   * Deep compares two dates based on start -end and comparison dates.
   * Expects the inputs to be the object returned by date picker.
   * @param {object} date1 - daterange object one to be compared
   * @param {object} date2 - dateRange object two to be compared
   * @returns {boolean} if date ranges are different.
  */
  isDateRangeChanged = (date1, date2) => {
    if ((date1 === date2) ||
      (_.isEmpty(date1) && _.isEmpty(date2))) {
      return false;
    }

    // Check if any one of the dateRange is undefined or empty
    if (!date1 || !date2 || _.isEmpty(date1) || _.isEmpty(date2)) {
      return true;
    }

    // Check if start and end dates are different
    if ((date1.start.getTime() !== date2.start.getTime()) ||
      (date1.end.getTime() !== date2.end.getTime())) {
      return true;
    }

    // Check if comparison dates are different
    if (date1.compared && date2.compared) {
      if ((date1.compared.start.getTime() !== date2.compared.start.getTime()) ||
        (date1.compared.end.getTime() !== date2.compared.end.getTime())) {
        return true;
      }
    }

    // Check if any one has comparison date
    if ((date1.compared && !date2.compared) ||
      (date2.compared && !date1.compared)) {
      return true;
    }

    return false;
  };

  getMetricsQuery = (primaryMetricSelectionId, secondaryMetricSelectionId) => {
    if (!this.inlineChartColumnMerticsEnabled) {
      return 'All';
    }
    const primaryMetricName = this.getMetricName(primaryMetricSelectionId);
    const secondaryMetricName = this.getMetricName(secondaryMetricSelectionId);
    const metricNames = _.chain([primaryMetricName, secondaryMetricName])
      .filter(name => name !== 'none')
      .uniq()
      .value();
    return metricNames.join(',');
  };

  formatPreferenceValue = (dateRange, preferenceValue) => {
    const granularityId = this.reevaluateGranularity(dateRange, preferenceValue.granularityId);
    if (_.isUndefined(granularityId)) {
      return;
    }
    const granularity = this.getGranularityEnum(granularityId);
    const metrics = this.getMetricsQuery(
      preferenceValue.primaryMetricId,
      preferenceValue.secondaryMetricId
    );
    const movingAverage = preferenceValue.showMovingAverage;
    // eslint-disable-next-line consistent-return
    return {
      Granularity: granularity,
      Metrics: metrics,
      MovingAverage: (!dateRange.compared) &&
      (granularityId === Defaults.graunlarityId) && movingAverage,
    };
  };
}
